/**
 * useShellTabs - the roster bar's verbs, with the turn order and the map kept in step.
 *
 * Wraps `useTabManagement`:
 * - NEW: the first person YOU put in is the one the panels point at, and a
 *   running tracker admits them with THE SHEET, not an id to look up - the
 *   roster does not hold them yet, and a lookup rolled initiative at +0 for
 *   every new target until v8.76.0.
 * - DUPLICATE: admitted under the SOURCE's sheet, which carries the talents
 *   a statblock derivation would lose.
 * - CLOSE: removing the COMBATANT, everywhere, AT ONCE (v8.88.0) - the
 *   tracker row, the marker and the document, the same three writes a
 *   recall makes. Dropping local state alone let the next snapshot put
 *   them straight back.
 *
 * No roundCountdown seeding: the rule is session-wide (v7.10.0).
 * Extracted from DeathWatchSimulator.
 */
import { useCallback, useEffect, useRef } from 'react';
import { characterFromStatblock } from '../engine/roster/factories';
import { brokenLine } from '../engine/roster/hordeTools';
import { hordeBeat } from '../engine/attackBeat';
import useTabManagement from './useTabManagement.js';

/**
 * How long a broken horde stays on the table, fleeing, before it is closed (ms): its plate's flicker (twenty members
 * 0.04 s apart, each 1 s: under 1.8 s) and the hot state's sync to the other screens (about 0.4 s), with room to spare.
 */
const FLEE_MS = 2600;

/** May a new arrival join the running order? */
const canTrack = (turnTracker, isLocked) => turnTracker.trackerState.active && !isLocked('turnTracker');

/** New tab: point at it if nothing is pointed at, and track it. */
function useNewTab(rawNewTab, { pointAtIfUnset, turnTracker, isLocked }) {
  return useCallback(() => {
    const newTab = rawNewTab();
    if (!newTab) return;
    pointAtIfUnset(newTab.id);
    if (!canTrack(turnTracker, isLocked)) return;
    turnTracker.addTabParticipant(newTab.id, { name: 'New Target', sheet: characterFromStatblock(newTab.target) });
  }, [rawNewTab, pointAtIfUnset, turnTracker, isLocked]);
}

/** Duplicate: track the copy under the source's own sheet. */
function useDuplicateTab(rawDuplicate, { tabs, turnTracker, isLocked, sheetForCombatant }) {
  return useCallback(
    (tabId, clone) => {
      const srcTab = tabs.find((t) => t.id === tabId);
      const newTab = rawDuplicate(tabId, clone);
      if (!newTab || !canTrack(turnTracker, isLocked)) return;
      turnTracker.addTabParticipant(newTab.id, {
        name: (srcTab?.label || 'Target') + (clone ? ' (Clone)' : ' (Copy)'),
        sheet: sheetForCombatant(tabId) ?? characterFromStatblock(newTab.target),
      });
    },
    [rawDuplicate, turnTracker, isLocked, tabs, sheetForCombatant],
  );
}

/**
 * The GM's horde tools (plan map-display, B; no rule): a split half joins a
 * running order under the source's sheet, as a copy does; a broken horde
 * flees - marked broken and its line said, its plate flickers out on every
 * screen, then the close, everywhere at once (plan design-gaps, P7).
 */
function useHordeTools(raw, close, args) {
  const { tabs, turnTracker, isLocked, sheetForCombatant, narrate, isHidden } = args;
  const { handleSplitHorde: rawSplit, canCloseTab, markHordeBroken } = raw;
  const handleSplitHorde = useCallback(
    (tabId) => {
      // A hidden horde is never split: the new half would be public, with its name (plan map-display, G1).
      const made = !isHidden?.(tabId) && rawSplit(tabId);
      if (!made) return;
      narrate?.(made.line, tabId, hordeBeat(tabId, 'split', Date.now()));
      if (!canTrack(turnTracker, isLocked)) return;
      const sheet = sheetForCombatant(tabId) ?? characterFromStatblock(made.target);
      turnTracker.addTabParticipant(made.id, { name: made.label, sheet });
    },
    [rawSplit, narrate, turnTracker, isLocked, sheetForCombatant, isHidden],
  );
  const flee = useFlight(close, canCloseTab);
  const handleBreakHorde = useCallback(
    (tabId) => {
      // Nothing to say unless it really goes: a close refused (locked, answering an attack) is no flight,
      // and a hidden horde's flight is no player's news; a horde already fleeing is not broken twice.
      const tab = tabs.find((t) => t.id === tabId);
      if (!canCloseTab(tabId) || isHidden?.(tabId) || tab?.target?.horde?.broken) return;
      markHordeBroken(tabId);
      narrate?.(brokenLine(tab?.label ?? 'The horde'), tabId, hordeBeat(tabId, 'break', Date.now()));
      flee(tabId);
    },
    [tabs, narrate, canCloseTab, isHidden, markHordeBroken, flee],
  );
  return { handleSplitHorde, handleBreakHorde };
}

/**
 * A broken horde's flight: closed once its flicker has played (FLEE_MS), by the close as it stands THEN - asked again
 * whether it may go (an attack declared on it meanwhile keeps it) - never by the one captured at the Break. A screen
 * that reloads mid-flight leaves the horde standing, ghosted under its BROKEN stencil, for the GM to close.
 */
function useFlight(close, canCloseTab) {
  const latest = useRef({ close, canCloseTab });
  useEffect(() => {
    latest.current = { close, canCloseTab };
  });
  return useCallback((tabId) => {
    setTimeout(() => {
      if (latest.current.canCloseTab(tabId)) latest.current.close(tabId);
    }, FLEE_MS);
  }, []);
}

/** Close: the tab, the tracker row, the marker and the document. */
function useCloseTab(rawClose, { turnTracker, removeToken, removeCombatant }) {
  return useCallback(
    (tabId) => {
      rawClose(tabId);
      if (turnTracker.isTabTracked(tabId)) turnTracker.removeParticipant('tab-' + tabId);
      removeToken(tabId);
      removeCombatant(tabId);
    },
    [rawClose, turnTracker, removeToken, removeCombatant],
  );
}

/**
 * @param {object} args - everything `useTabManagement` takes (tabs, setTabs,
 *   activeTab, setActiveTab, setTabCtx, setRenamingTab, isLocked,
 *   syncTargetToFirestore, updateTab, sessionId, sync, isDefencePending),
 *   plus pointAtIfUnset, turnTracker, sheetForCombatant, removeToken,
 *   removeCombatant, narrate (text, tabId, beat) - the story lane, for the horde tools, with the split or break
 *   beat the map plays - and isHidden.
 */
export default function useShellTabs(args) {
  const raw = useTabManagement({
    tabs: args.tabs,
    setTabs: args.setTabs,
    activeTab: args.activeTab,
    setActiveTab: args.setActiveTab,
    setTabCtx: args.setTabCtx,
    setRenamingTab: args.setRenamingTab,
    isLocked: args.isLocked,
    syncTargetToFirestore: args.syncTargetToFirestore,
    updateTab: args.updateTab,
    sessionId: args.sessionId,
    sync: { tgtSyncTimerRef: args.sync.tgtSyncTimerRef, lastTgtJsonRef: args.sync.lastTgtJsonRef },
    isDefencePending: args.isDefencePending,
  });
  const handleCloseTab = useCloseTab(raw.handleCloseTab, args);
  return {
    handleNewTab: useNewTab(raw.handleNewTab, args),
    handleDuplicateTab: useDuplicateTab(raw.handleDuplicateTab, args),
    handleCloseTab,
    ...useHordeTools(raw, handleCloseTab, args),
    handleRenameTab: raw.handleRenameTab,
    handleTabDrop: raw.handleTabDrop,
  };
}
